/**
 * DataTypeService for managing type-specific data operations
 */
import versionService from './versionService';
import RecommendationService from './recommendationService';
import { createErrorLogger, ErrorType } from '../utils/logger';

const logger = createErrorLogger('DataTypeService');

class DataTypeService {
  /**
   * Save CV competencies
   * @param {string} userId - User ID
   * @param {object} competencies - Competencies data
   * @returns {Promise<object>} Save result
   */
  async saveCompetencies(userId, competencies) {
    logger.info('saveCompetencies', `Saving CV competencies for user ${userId}`);
    try {
      const result = await versionService.saveVersionedData(
        userId,
        'cv_analyzer',
        'result.json',
        competencies
      );
      logger.info('saveCompetencies', 'CV competencies saved successfully');
      return result;
    } catch (error) {
      logger.error('saveCompetencies', 'Error saving CV competencies', error, ErrorType.DATA_SAVE);
      throw error;
    }
  }

  /**
   * Load CV competencies
   * @param {string} userId - User ID
   * @param {string|null} version - Version to load
   * @returns {Promise<object>} Competencies data
   */
  async loadCompetencies(userId, version = null) {
    logger.info('loadCompetencies', `Loading CV competencies for user ${userId}, version: ${version}`);
    try {
      const data = await versionService.loadVersionedData(
        userId,
        'cv_analyzer',
        'result.json',
        version
      );
      logger.info('loadCompetencies', 'CV competencies loaded successfully');
      return data || null;
    } catch (error) {
      logger.error('loadCompetencies', 'Error loading CV competencies', error, ErrorType.DATA_LOAD);
      throw error;
    }
  }

  /**
   * Save job skills
   * @param {string} userId - User ID
   * @param {object} skills - Skills data
   * @returns {Promise<object>} Save result
   */
  async saveJobSkills(userId, skills) {
    logger.info('saveJobSkills', `Saving job skills for user ${userId}`);

    // If skills contains position_id, extract it and save separately
    if (skills.position_id) {
      logger.info('saveJobSkills', `Found position_id: ${skills.position_id}`);
      // Save position ID to a separate file for tracking
      try {
        await versionService.saveVersionedData(
          userId,
          'job_analyzer',
          'current_position.json',
          { position_id: skills.position_id, timestamp: new Date().toISOString() },
          'User',
          { preventVersion: true }
        );
      } catch (error) {
        logger.error('saveJobSkills', 'Error saving position ID', error, ErrorType.DATA_SAVE);
      }
    }

    try {
      const result = await versionService.saveVersionedData(
        userId,
        'job_analyzer',
        'result.json',
        skills
      );
      logger.info('saveJobSkills', 'Job skills saved successfully');
      return result;
    } catch (error) {
      logger.error('saveJobSkills', 'Error saving job skills', error, ErrorType.DATA_SAVE);
      throw error;
    }
  }

  /**
   * Load job skills
   * @param {string} userId - User ID
   * @param {string|null} version - Version to load
   * @returns {Promise<object>} Skills data
   */
  async loadJobSkills(userId, version = null) {
    logger.info('loadJobSkills', `Loading job skills for user ${userId}, version: ${version}`);
    try {
      const data = await versionService.loadVersionedData(
        userId,
        'job_analyzer',
        'result.json',
        version
      );
      logger.info('loadJobSkills', 'Job skills loaded successfully');
      return data || null;
    } catch (error) {
      logger.error('loadJobSkills', 'Error loading job skills', error, ErrorType.DATA_LOAD);
      throw error;
    }
  }

  /**
   * Save job skills with version metadata
   * @param {string} userId - User ID
   * @param {object} skills - Skills data
   * @param {object} versionMetadata - Version metadata
   * @returns {Promise<object>} Save result
   */
  async saveJobSkillWithVersion(userId, skills, versionMetadata) {
    logger.info('saveJobSkillWithVersion', `Saving job skills with version for user ${userId}`);

    try {
      // First save the job skills data
      const saveResult = await this.saveJobSkills(userId, skills);

      if (saveResult.status === 'success') {
        // Then save the version metadata if provided
        if (versionMetadata) {
          logger.info('saveJobSkillWithVersion', 'Saving version metadata:', versionMetadata);
          try {
            await versionService.saveVersionLogEntry(
              versionMetadata.version || Date.now(),
              versionMetadata.description || '',
              userId,
              'job_analyzer'
            );
          } catch (error) {
            logger.error('saveJobSkillWithVersion', 'Error saving version metadata', error, ErrorType.DATA_SAVE);
          }
        }
      }

      logger.info('saveJobSkillWithVersion', 'Job skills saved with version successfully');
      return saveResult;
    } catch (error) {
      logger.error('saveJobSkillWithVersion', 'Error saving job skills with version', error, ErrorType.DATA_SAVE);
      throw error;
    }
  }

  /**
   * Save CV competencies with version metadata
   * @param {string} userId - User ID
   * @param {object} competencies - Competencies data
   * @param {object} versionMetadata - Version metadata
   * @returns {Promise<object>} Save result
   */
  async saveCompetenciesWithVersion(userId, competencies, versionMetadata) {
    logger.info('saveCompetenciesWithVersion', `Saving competencies with version for user ${userId}`);

    try {
      // First save the competencies data
      const saveResult = await this.saveCompetencies(userId, competencies);

      if (saveResult.status === 'success') {
        // Then save the version metadata if provided
        if (versionMetadata) {
          logger.info('saveCompetenciesWithVersion', 'Saving version metadata:', versionMetadata);
          try {
            await versionService.saveVersionLogEntry(
              versionMetadata.version || Date.now(),
              versionMetadata.description || '',
              userId,
              'cv_analyzer'
            );
          } catch (error) {
            logger.error('saveCompetenciesWithVersion', 'Error saving version metadata', error, ErrorType.DATA_SAVE);
          }
        }
      }

      logger.info('saveCompetenciesWithVersion', 'Competencies saved with version successfully');
      return saveResult;
    } catch (error) {
      logger.error('saveCompetenciesWithVersion', 'Error saving competencies with version', error, ErrorType.DATA_SAVE);
      throw error;
    }
  }

  /**
   * Save training recommendations
   * @param {string} userId - User ID
   * @param {object} recommendations - Recommendations data
   * @returns {Promise<object>} Save result
   */
  async saveTrainingRecommendations(userId, recommendations) {
    logger.info('saveTrainingRecommendations', `Saving training recommendations for user ${userId}`);
    try {
      const result = await versionService.saveVersionedData(
        userId,
        'training',
        'recommendations.json',
        recommendations
      );
      logger.info('saveTrainingRecommendations', 'Training recommendations saved successfully');
      return result;
    } catch (error) {
      logger.error('saveTrainingRecommendations', 'Error saving training recommendations', error, ErrorType.DATA_SAVE);
      throw error;
    }
  }

  /**
   * Load training recommendations
   * @param {string} userId - User ID
   * @param {string|null} version - Version to load
   * @returns {Promise<object>} Recommendations data
   */
  async loadTrainingRecommendations(userId, version = null) {
    logger.info('loadTrainingRecommendations', `Loading training recommendations for user ${userId}, version: ${version}`);
    try {
      const data = await versionService.loadVersionedData(
        userId,
        'training',
        'recommendations.json',
        version
      );
      logger.info('loadTrainingRecommendations', 'Training recommendations loaded successfully');
      return data || null;
    } catch (error) {
      logger.error('loadTrainingRecommendations', 'Error loading training recommendations', error, ErrorType.DATA_LOAD);
      return null;
    }
  }

  /**
   * Save company research data (admin only)
   * @param {object} data - Company data
   * @returns {Promise<object>} Save result
   */
  async saveCompanyData(data) {
    logger.info('saveCompanyData', 'Saving company research data');

    try {
      // Company data is always saved as admin user
      const result = await versionService.saveVersionedData(
        'admin',
        'company_research',
        'result.json',
        data,
        'Admin'
      );

      logger.info('saveCompanyData', 'Company data save result:', result);
      return result;
    } catch (error) {
      logger.error('saveCompanyData', 'Error saving company data', error, ErrorType.DATA_SAVE);
      return { status: 'error', message: error.message };
    }
  }

  /**
   * Load company research data
   * @returns {Promise<object>} Company data
   */
  async loadCompanyData() {
    logger.info('loadCompanyData', 'Loading company research data');

    try {
      const data = await versionService.loadVersionedData(
        'admin',
        'company_research',
        'result.json'
      );

      logger.info('loadCompanyData', 'Company data loaded:', data);
      return data || null;
    } catch (error) {
      logger.error('loadCompanyData', 'Error loading company data', error, ErrorType.DATA_LOAD);
      throw error;
    }
  }

  /**
   * Get type-specific component and filename mapping
   * @param {string} type - Data type
   * @returns {object} Component and filename
   */
  getTypeMapping(type) {
    const mappings = {
      'cv': { component: 'cv_analyzer', filename: 'result.json' },
      'job': { component: 'job_analyzer', filename: 'result.json' },
      'company': { component: 'company_research', filename: 'result.json' },
      'training': { component: 'training', filename: 'recommendations.json' },
      'settings': { component: 'settings', filename: 'settings.json' },
      'preferences': { component: 'preferences', filename: 'preferences.json' }
    };

    if (!mappings[type]) {
      throw new Error(`Unsupported data type mapping requested: ${type}`);
    }
    return mappings[type];
  }

  /**
   * Generate training recommendations based on gaps
   * @param {string} userId - User ID
   * @param {object} cvData - CV analysis data
   * @param {object} jobData - Job analysis data
   * @returns {object} Training recommendations
   */
  async generateTrainingRecommendations(userId, cvData, jobData) {
    logger.info('generateTrainingRecommendations', 'Generating training recommendations');

    try {
      if (!cvData || !jobData) {
        throw new Error('Missing CV or job data for training recommendations');
      }

      // Delegate to AnalyzerService for actual recommendation generation
      const recommendations = await RecommendationService.generateRecommendations(userId);
      logger.info(
        'generateTrainingRecommendations',
        `Generated recommendations (immediate=${recommendations.immediate?.length || 0}, future=${recommendations.future?.length || 0}, ai=${recommendations.ai?.length || 0})`
      );

      return recommendations;
    } catch (error) {
      logger.error('generateTrainingRecommendations', 'Error generating training recommendations', error, ErrorType.ANALYSIS);
      throw error;
    }
  }
}

// Create singleton instance
const dataTypeService = new DataTypeService();

// Export singleton instance
export default dataTypeService;
